import * as React from "react";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Badge } from "@/common/components/ui/badge";
import { message, type TableColumnsType } from "antd";
import AntDTable from "@/common/components/ui/antd-table";
import CommonModal from "@/common/components/ui/modal";
import {
  History,
  Save,
  ArrowLeft,
  Clock,
  User,
  Coins,
  AlertCircle,
  Percent,
  Download,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import { GoldService } from "@/services/APIs";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const CommissionControl = () => {
  const [commissions, setCommissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [goldCommission, setGoldCommission] = useState("");
  const [silverCommission, setSilverCommission] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    id: string;
    metal: string;
    rate: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCommissions();
  }, [page, pageSize]);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const response = await GoldService.getCommission(page, pageSize);
      const data = response?.data ?? [];
      const totalCount = response?.data?.length ?? 0;
      setCommissions(Array.isArray(data) ? data : []);
      setTotal(totalCount);

      const goldEntry = data.find(
        (p: any) => p.metal?.toUpperCase() === "GOLD"
      );
      const silverEntry = data.find(
        (p: any) => p.metal?.toUpperCase() === "SILVER"
      );

      if (goldEntry) {
        setGoldCommission(goldEntry.commission_percentage?.toString() ?? "");
      } else {
        setGoldCommission("");
      }
      if (silverEntry) {
        setSilverCommission(
          silverEntry.commission_percentage?.toString() ?? ""
        );
      } else {
        setSilverCommission("");
      }
    } catch (_err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch commission history",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSelectChange = (keys: React.Key[], rows: any[]) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(item => ({
      Metal: item.metal?.toUpperCase(),
      "Commission (%)": parseFloat(item.commission_percentage ?? 0).toFixed(2),
      "Effective Date": item.updated_on
        ? new Date(item.updated_on).toLocaleString()
        : "-",
      "Updated By": item.updated_by ?? "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Commissions");

    XLSX.writeFile(
      wb,
      `commission_history_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    notify.success("Exported", `${rows.length} record(s) exported.`);
  };

  const initiateUpdate = (metal: string) => {
    const rate = metal === "Gold" ? goldCommission : silverCommission;
    const selectedCommission = commissions.find(
      c => c?.metal?.toUpperCase() === metal.toUpperCase()
    );

    if (!rate || isNaN(parseFloat(rate))) {
      toast({
        variant: "destructive",
        title: "Validation",
        description: "Please enter a valid commission percentage",
      });
      return;
    }

    if (parseFloat(rate) < 0 || parseFloat(rate) > 100) {
      toast({
        variant: "destructive",
        title: "Validation",
        description: "Commission must be between 0% and 100%",
      });
      return;
    }

    if (!selectedCommission?.id) {
      toast({
        variant: "destructive",
        title: "Validation",
        description: "Unable to find commission record to update",
      });
      return;
    }

    setPendingUpdate({ id: selectedCommission.id, metal, rate });
    setIsConfirmOpen(true);
  };

  const commissionHistoryColumns: TableColumnsType<any> = [
    {
      title: "Metal Type",
      dataIndex: "metal",
      key: "metal",
      width: 140,
      render: (val: string) => {
        const metalName = val?.toUpperCase() === "GOLD" ? "GOLD" : "SILVER";
        return (
          <Badge
            className={`font-bold transition-all ${
              metalName === "GOLD"
                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {metalName}
          </Badge>
        );
      },
    },
    {
      title: "Commission (%)",
      dataIndex: "commission_percentage",
      key: "commission_percentage",
      width: 160,
      render: (val: any) => (
        <span className="text-base font-bold text-secondary">
          {parseFloat(val ?? 0).toFixed(2)}%
        </span>
      ),
    },
    {
      title: "Effective Date",
      dataIndex: "updated_on",
      key: "updated_on",
      width: 200,
      render: (val: string) => (
        <div className="flex items-center gap-1.5 font-medium text-muted-foreground">
          <Clock className="h-3 w-3" />
          {val ? new Date(val).toLocaleString() : "-"}
        </div>
      ),
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (val: string) => (
        <div className="float-end flex gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
            <User className="h-3 w-3 text-muted-foreground" />
          </div>
          <span className="text-sm font-medium text-secondary">
            {val ?? "-"}
          </span>
        </div>
      ),
    },
  ];

  const handleConfirmUpdate = async () => {
    if (!pendingUpdate) {
      return;
    }
    const { id, metal, rate } = pendingUpdate;

    try {
      const response = await GoldService.updateCommissionById(id, {
        metal: metal,
        commission_percentage: rate,
      });
      if (response?.success) {
        message.success(response?.message ?? "Commission updated successfully");
        setIsConfirmOpen(false);
        setPendingUpdate(null);
        fetchCommissions();
      } else {
        message.error(response?.message ?? "Failed to update commission");
      }
    } catch (err: any) {
      message.error(err?.message ?? "Failed to update commission");
    }
  };

  return (
    <>
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto w-full max-w-7xl">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-5">
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() =>
                  navigate(finEaseHomeNavigationRoutes.PHYSICAL_GOLD_DASHBOARD)
                }
                className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  Commission Control
                </h1>
                <p className="mt-0.5 text-sm font-medium text-slate-500">
                  Set and manage commission rates for gold and silver
                  transactions
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                className="gap-2 border-slate-200 bg-white hover:bg-slate-50"
                onClick={handleExport}
                disabled={selectedRows.length === 0}
              >
                <Download className="h-4 w-4" />
                Export
                {selectedRows.length > 0 && (
                  <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-slate-600">
                    {selectedRows.length}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Gold Commission */}
          <Card className="overflow-hidden border-t-4 border-none border-amber-500 bg-white shadow-sm">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-50/80">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-amber-900">
                  <Coins className="h-5 w-5 text-amber-600" /> Gold Commission
                </CardTitle>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <Percent className="h-5 w-5 text-amber-700" />
                </div>
              </div>
              <CardDescription className="font-medium text-amber-800/70">
                Set the commission percentage for gold transactions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-col gap-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Commission Rate (%) *
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="Enter gold commission %"
                      className="h-11 border-gray-200 pr-10 text-lg font-bold placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      value={goldCommission}
                      onChange={e => setGoldCommission(e.target.value)}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg font-bold text-slate-400">
                      %
                    </span>
                  </div>
                  <Button
                    className="h-11 gap-2 bg-amber-600 px-6 text-white hover:bg-amber-700"
                    onClick={() => initiateUpdate("Gold")}
                  >
                    <Save className="h-4 w-4" /> Update
                  </Button>
                </div>
              </div>
              {goldCommission && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2">
                  <span className="text-xs font-bold text-amber-800">
                    Current Rate:
                  </span>
                  <Badge className="bg-amber-100 text-sm font-black text-amber-700 hover:bg-amber-200">
                    {parseFloat(goldCommission).toFixed(2)}%
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Silver Commission */}
          <Card className="overflow-hidden border-t-4 border-none border-slate-400 bg-white shadow-sm">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-50/80">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  <Coins className="h-5 w-5 text-slate-500" /> Silver Commission
                </CardTitle>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <Percent className="h-5 w-5 text-slate-600" />
                </div>
              </div>
              <CardDescription className="font-medium text-slate-800/70">
                Set the commission percentage for silver transactions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-col gap-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Commission Rate (%) *
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="Enter silver commission %"
                      className="h-11 border-gray-200 pr-10 text-lg font-bold placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      value={silverCommission}
                      onChange={e => setSilverCommission(e.target.value)}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg font-bold text-slate-400">
                      %
                    </span>
                  </div>
                  <Button
                    className="h-11 gap-2 bg-slate-700 px-6 text-white hover:bg-slate-800"
                    onClick={() => initiateUpdate("Silver")}
                  >
                    <Save className="h-4 w-4" /> Update
                  </Button>
                </div>
              </div>
              {silverCommission && (
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2">
                  <span className="text-xs font-bold text-slate-700">
                    Current Rate:
                  </span>
                  <Badge className="bg-slate-100 text-sm font-black text-slate-700 hover:bg-slate-200">
                    {parseFloat(silverCommission).toFixed(2)}%
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Commission History */}
        <Card className="border-none bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 py-4">
            <CardTitle className="flex items-center gap-2 text-lg font-bold text-secondary">
              <History className="h-5 w-5 text-primary" />
              Commission Rate Logs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AntDTable
              columns={commissionHistoryColumns}
              data={commissions}
              loading={loading}
              rowKey={(_: any, index: any) => `commission-${index}`}
              rowSelection={{
                selectedRowKeys,
                onChange: onSelectChange,
              }}
              pagination={
                commissions.length >= 10 || total >= 10
                  ? {
                      pageSize,
                      total,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50", "100"],
                      onChange: (nextPage, nextSize) => {
                        setPage(nextPage);
                        setPageSize(nextSize ?? 10);
                      },
                      showTotal: (totalCount, range) =>
                        `${range[0]}-${range[1]} of ${totalCount} records`,
                    }
                  : false
              }
              emptyText="No commission records found"
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <AlertCircle className="h-5 w-5 shrink-0 text-primary" />
          <p className="text-xs font-medium leading-relaxed text-secondary">
            <span className="font-bold">Commission Policy:</span> Updated
            commission rates will apply to all new orders placed after the
            change. Existing orders will retain their original commission rates.
            Changes are logged and auditable.
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      <CommonModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setPendingUpdate(null);
        }}
        width="max-w-md"
        padding="p-6"
      >
        <div className="space-y-4 pr-8">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
            <h3 className="text-lg font-bold text-secondary">
              Confirm Commission Update
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to update the{" "}
            <strong>{pendingUpdate?.metal}</strong> commission to{" "}
            <strong>{pendingUpdate?.rate}%</strong>? This change will apply to
            all new orders placed after the update.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsConfirmOpen(false);
                setPendingUpdate(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={handleConfirmUpdate}
            >
              Confirm & Update
            </Button>
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default CommissionControl;
